import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  PortalUser as AjaxPortalUser,
  getPortalUsers,
  upsertPortalUser,
  deletePortalUser as ajaxDeletePortalUser,
} from '@client/components/ajax';
import { Async, toAsyncFetched } from '@client/new/helpers/types';
import { toPortalUsers } from './PortalUserList.mappers';
import {
  PortalUser,
  PortalUserInput,
  PortalUserListModel,
} from './PortalUserList.types';

const QUERY_KEY = ['admin', 'portalUsers'] as const;

export const usePortalUserListModel = (): Async<PortalUserListModel> => {
  const query = useQuery<AjaxPortalUser[] | undefined>(QUERY_KEY, () =>
    getPortalUsers(),
  );

  const data = useMemo((): Async<PortalUser[]> => {
    if (query.error) {
      const message =
        query.error instanceof Error
          ? query.error.message
          : 'Cannot load users';
      return { type: 'fetch-error', value: message };
    }
    if (query.data) return toAsyncFetched(toPortalUsers(query.data));
    if (query.isLoading) return { type: 'fetching' };
    return { type: 'not-fetched' };
  }, [query.data, query.isLoading, query.error]);

  if (data.type !== 'fetched') return data;

  const togglePortalUser: PortalUserListModel['togglePortalUser'] = async (
    id,
  ) => {
    const fresh = (await getPortalUsers(id)) as AjaxPortalUser | undefined;
    if (!fresh) {
      throw new Error('Could not load user before toggle');
    }
    await upsertPortalUser({ ...fresh, isActive: !fresh.isActive });
    await query.refetch();
  };

  const savePortalUser = async ({ username, ...input }: PortalUserInput) => {
    await upsertPortalUser({ ...input, username: username ?? undefined });
    await query.refetch();
  };

  const deletePortalUser: PortalUserListModel['deletePortalUser'] = async (
    id,
  ) => {
    await ajaxDeletePortalUser(id);
    await query.refetch();
  };

  const refetch: PortalUserListModel['refetch'] = async () => {
    await query.refetch();
  };

  return toAsyncFetched<PortalUserListModel>({
    portalUsers: data.value,
    savePortalUser,
    togglePortalUser,
    deletePortalUser,
    refetch,
  });
};
